import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Save,
  ChevronDown,
  ChevronRight,
  Edit2,
  Eye,
  EyeOff,
  Upload,
  ImageIcon,
  X
} from 'lucide-react';

import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatPrice } from '@/lib/formatPrice';
import { resolveImageUrl } from '@/lib/utils';
import {
  DEFAULT_WEEKDAY_AVAILABILITY,
  WEEKDAY_OPTIONS,
  formatWeekdayAvailabilityLabel,
  normalizeAvailableWeekdays,
} from '@/lib/weekdayAvailability';

const ADMIN_API = `${window.location.origin}/menu-digitale/api/admin`;

const BADGE_OPTIONS = [
  { value: 'consigliato', label: 'Consigliato' },
  { value: 'vegetariano', label: 'Vegetariano' },
  { value: 'senza-glutine', label: 'Senza Glutine' },
  { value: 'novita', label: 'Novita' },
];

const parsePrice = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(num)) return null;
  return Math.round(num * 100) / 100;
};

const createEmptyCategory = (sortOrder = 0) => ({
  id: `tmp-cat-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  name: '',
  slug: '',
  description: '',
  sort_order: sortOrder,
  is_active: true,
  items: [],
});

const createEmptyItem = (sortOrder = 0) => ({
  id: `tmp-item-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  name: '',
  description: '',
  price_type: 'portion',
  price_value: '',
  show_price: true,
  image_url: null,
  badges: [],
  sort_order: sortOrder,
  is_active: true,
  available_weekdays: [...DEFAULT_WEEKDAY_AVAILABILITY],
});

const WeekdaySelector = ({ value, onChange }) => {
  const selected = normalizeAvailableWeekdays(value);

  const toggleWeekday = (weekday) => {
    const next = selected.includes(weekday)
      ? selected.filter((entry) => entry !== weekday)
      : [...selected, weekday];

    onChange(normalizeAvailableWeekdays(next.length > 0 ? next : DEFAULT_WEEKDAY_AVAILABILITY));
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Disponibilita per giorni</Label>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
        {WEEKDAY_OPTIONS.map((day) => {
          const isSelected = selected.includes(day.value);

          return (
            <Button
              key={day.value}
              type="button"
              variant={isSelected ? 'default' : 'outline'}
              className={isSelected ? 'gradient-instagram text-white' : ''}
              onClick={() => toggleWeekday(day.value)}
            >
              {day.short}
            </Button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        {formatWeekdayAvailabilityLabel(selected)}
      </p>
    </div>
  );
};

export default function AdminFullMenu() {
  const auth = useAuth();

  const token =
    auth?.token ||
    auth?.user?.token ||
    auth?.currentUser?.token ||
    localStorage.getItem('sapori1989_admin_auth') ||
    null;

  const authHeaders = useCallback(
    (extra = {}) => ({
      ...extra,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [newCategoryDialog, setNewCategoryDialog] = useState(false);
  const [editCategoryDialog, setEditCategoryDialog] = useState(null);

  const [newItemDialog, setNewItemDialog] = useState(false);
  const [editItemDialog, setEditItemDialog] = useState(null);

  const [newCategory, setNewCategory] = useState({
    name: '',
    slug: '',
    description: '',
    is_active: true,
  });

  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price_type: 'portion',
    price_value: '',
    show_price: true,
    image_url: null,
    badges: [],
    is_active: true,
    available_weekdays: [...DEFAULT_WEEKDAY_AVAILABILITY],
  });

  const [targetCategoryId, setTargetCategoryId] = useState(null);

  const fetchFullMenu = useCallback(async () => {
    try {
      const response = await fetch(`${ADMIN_API}/full-menu/index.php`, {
        headers: authHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
          data?.detail ||
          data?.error ||
          'Errore nel caricamento del menu completo'
        );
      }

      const fetchedCategories = Array.isArray(data?.categories) ? data.categories : [];

      const normalized = fetchedCategories.map((category, catIndex) => ({
        ...category,
        sort_order: Number(category.sort_order ?? catIndex + 1),
        is_active: !!category.is_active,
        items: Array.isArray(category.items)
          ? category.items.map((item, itemIndex) => ({
              ...item,
              sort_order: Number(item.sort_order ?? itemIndex + 1),
              show_price: item.show_price !== false,
              is_active: item.is_active !== false,
              badges: Array.isArray(item.badges) ? item.badges : [],
              available_weekdays: normalizeAvailableWeekdays(item.available_weekdays),
            }))
          : [],
      }));

      setCategories(normalized);
      setHasChanges(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!token) return;
    fetchFullMenu();
  }, [token, fetchFullMenu]);

  const uploadImage = async (file) => {
    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${ADMIN_API}/upload-menu-item-image.php`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: formData,
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || data?.error || 'Errore nel caricamento immagine');
      }

      const imageUrl = data?.url || data?.path || null;
      if (!imageUrl) {
        throw new Error('URL immagine non restituito dal server');
      }

      return imageUrl;
    } catch (error) {
      toast.error(error.message || 'Errore nel caricamento immagine');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const deleteImage = async (url) => {
    if (!url) return true;

    try {
      const response = await fetch(
        `${ADMIN_API}/upload-menu-item-image.php?url=${encodeURIComponent(url)}`,
        {
          method: 'DELETE',
          headers: authHeaders(),
          credentials: 'include',
        }
      );

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || data?.error || 'Errore nella rimozione immagine');
      }

      return true;
    } catch (error) {
      toast.error(error.message || 'Errore nella rimozione immagine');
      return false;
    }
  };

  const handleNewItemImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await uploadImage(file);
    if (url) {
      setNewItem((prev) => ({ ...prev, image_url: url }));
      toast.success('Immagine caricata!');
    }

    e.target.value = '';
  };

  const handleEditItemImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await uploadImage(file);
    if (url) {
      setEditItemDialog((prev) => ({
        ...prev,
        item: { ...prev.item, image_url: url },
      }));
      toast.success('Immagine caricata!');
    }

    e.target.value = '';
  };

  const saveFullMenu = async () => {
    setSaving(true);

    try {
      const payload = {
        categories: categories.map((category, catIndex) => ({
          name: category.name?.trim() || '',
          slug: category.slug?.trim() || '',
          description: category.description?.trim() || '',
          sort_order: catIndex + 1,
          is_active: !!category.is_active,
          items: (Array.isArray(category.items) ? category.items : []).map((item, itemIndex) => ({
            name: item.name?.trim() || '',
            description: item.description?.trim() || '',
            price_type: item.price_type || 'portion',
            price_value: item.price_value === '' || item.price_value === null ? null : parsePrice(item.price_value),
            show_price: !!item.show_price,
            image_url: item.image_url || null,
            badges: Array.isArray(item.badges) ? item.badges : [],
            sort_order: itemIndex + 1,
            is_active: !!item.is_active,
            available_weekdays: normalizeAvailableWeekdays(item.available_weekdays),
          })),
        })),
      };

      const response = await fetch(`${ADMIN_API}/full-menu/save.php`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || data?.error || 'Errore nel salvataggio');
      }

      toast.success('Menu completo salvato!');
      setHasChanges(false);
      fetchFullMenu();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    if (!newCategory.name.trim()) {
      toast.error('Inserisci un nome categoria');
      return;
    }

    const nextCategory = {
      ...createEmptyCategory(categories.length + 1),
      name: newCategory.name.trim(),
      slug: newCategory.slug.trim(),
      description: newCategory.description.trim(),
      is_active: !!newCategory.is_active,
    };

    setCategories((prev) => [...prev, nextCategory]);
    setHasChanges(true);
    setNewCategoryDialog(false);
    setNewCategory({
      name: '',
      slug: '',
      description: '',
      is_active: true,
    });
    toast.success('Categoria aggiunta!');
  };

  const updateCategory = (categoryId, updates) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId ? { ...cat, ...updates } : cat
      )
    );
    setHasChanges(true);
  };

  const deleteCategory = (categoryId) => {
    if (!window.confirm('Eliminare questa categoria e tutti i suoi piatti?')) return;

    setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
    setHasChanges(true);
    toast.success('Categoria eliminata!');
  };

  const moveCategory = (categoryId, direction) => {
    setCategories((prev) => {
      const arr = [...prev];
      const idx = arr.findIndex((cat) => cat.id === categoryId);
      if (idx < 0) return prev;

      const nextIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (nextIdx < 0 || nextIdx >= arr.length) return prev;

      [arr[idx], arr[nextIdx]] = [arr[nextIdx], arr[idx]];
      return arr.map((cat, i) => ({ ...cat, sort_order: i + 1 }));
    });
    setHasChanges(true);
  };

  const addItem = () => {
    if (!targetCategoryId) {
      toast.error('Nessuna categoria selezionata');
      return;
    }

    if (!newItem.name.trim()) {
      toast.error('Inserisci un nome piatto');
      return;
    }

    const priceValue = parsePrice(newItem.price_value);
    if (newItem.price_value !== '' && priceValue === null) {
      toast.error('Prezzo non valido');
      return;
    }

    if (priceValue !== null && (priceValue <= 0 || priceValue > 999.99)) {
      toast.error('Il prezzo deve essere tra 0.01 e 999.99');
      return;
    }

    const nextItem = {
      ...createEmptyItem(),
      name: newItem.name.trim(),
      description: newItem.description.trim(),
      price_type: newItem.price_type,
      price_value: newItem.price_value === '' ? '' : priceValue,
      show_price: !!newItem.show_price,
      image_url: newItem.image_url || null,
      badges: Array.isArray(newItem.badges) ? newItem.badges : [],
      is_active: !!newItem.is_active,
      available_weekdays: normalizeAvailableWeekdays(newItem.available_weekdays),
    };

    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id !== targetCategoryId) return cat;
        const items = Array.isArray(cat.items) ? cat.items : [];
        return {
          ...cat,
          items: [...items, { ...nextItem, sort_order: items.length + 1 }],
        };
      })
    );

    setHasChanges(true);
    setNewItemDialog(false);
    setTargetCategoryId(null);
    setNewItem({
      name: '',
      description: '',
      price_type: 'portion',
      price_value: '',
      show_price: true,
      image_url: null,
      badges: [],
      is_active: true,
      available_weekdays: [...DEFAULT_WEEKDAY_AVAILABILITY],
    });
    toast.success('Piatto aggiunto!');
  };

  const updateItem = (categoryId, itemId, updates) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              items: (Array.isArray(cat.items) ? cat.items : []).map((item) =>
                item.id === itemId ? { ...item, ...updates } : item
              ),
            }
          : cat
      )
    );
    setHasChanges(true);
  };

  const deleteItem = (categoryId, itemId) => {
    if (!window.confirm('Eliminare questo piatto?')) return;

    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              items: (Array.isArray(cat.items) ? cat.items : []).filter((item) => item.id !== itemId),
            }
          : cat
      )
    );
    setHasChanges(true);
    toast.success('Piatto eliminato!');
  };

  const moveItem = (categoryId, itemId, direction) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id !== categoryId) return cat;

        const items = [...(Array.isArray(cat.items) ? cat.items : [])];
        const idx = items.findIndex((item) => item.id === itemId);
        if (idx < 0) return cat;

        const nextIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (nextIdx < 0 || nextIdx >= items.length) return cat;

        [items[idx], items[nextIdx]] = [items[nextIdx], items[idx]];

        return {
          ...cat,
          items: items.map((item, i) => ({ ...item, sort_order: i + 1 })),
        };
      })
    );
    setHasChanges(true);
  };

  const renderPriceDisplay = (item) => {
    if (item.price_value === null || item.price_value === undefined || item.price_value === '') {
      return <span className="text-muted-foreground text-xs">Senza prezzo</span>;
    }

    const label =
      item.price_type === 'fixed'
        ? `${formatPrice(item.price_value)} fisso`
        : `${formatPrice(item.price_value)} / 300g`;

    if (!item.show_price) {
      return (
        <span className="text-muted-foreground text-xs flex items-center gap-1">
          <EyeOff className="w-3 h-3" />
          {label} (nascosto)
        </span>
      );
    }

    return (
      <Badge variant="secondary" className="text-xs">
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Menu Completo</h1>
            <p className="text-muted-foreground">
              Gestisci categorie fisse, piatti e bevande del menu completo
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setNewCategoryDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nuova Categoria
            </Button>

            <Button
              onClick={saveFullMenu}
              disabled={saving || uploadingImage}
              className="gradient-instagram text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'Salva Menu Completo'}
            </Button>
          </div>
        </div>

        {categories.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground mb-4">Nessuna categoria presente</p>
              <Button onClick={() => setNewCategoryDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crea la prima categoria
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {categories.map((category, catIdx) => {
              const items = Array.isArray(category.items) ? category.items : [];

              return (
                <Collapsible key={category.id} defaultOpen>
                  <Card className="border-dashed">
                    <CollapsibleTrigger asChild>
                      <CardHeader className="py-3 cursor-pointer hover:bg-muted/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ChevronRight className="w-4 h-4 transition-transform duration-200 collapsible-open:rotate-90" />
                            <span className="font-semibold">{category.name || 'Categoria senza nome'}</span>
                            <Badge variant="secondary" className="ml-2">
                              {items.length} piatti
                            </Badge>
                            {!category.is_active && (
                              <Badge variant="outline" className="text-xs">
                                Disattiva
                              </Badge>
                            )}
                          </div>

                          <div
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => moveCategory(category.id, 'up')}
                              disabled={catIdx === 0}
                            >
                              <ChevronDown className="w-4 h-4 rotate-180" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => moveCategory(category.id, 'down')}
                              disabled={catIdx === categories.length - 1}
                            >
                              <ChevronDown className="w-4 h-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setEditCategoryDialog({ ...category })}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => deleteCategory(category.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0 space-y-2">
                        {items.map((item, itemIdx) => (
                          <div
                            key={item.id}
                            className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg group"
                          >
                            <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => moveItem(category.id, item.id, 'up')}
                                disabled={itemIdx === 0}
                              >
                                <ChevronDown className="w-3 h-3 rotate-180" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => moveItem(category.id, item.id, 'down')}
                                disabled={itemIdx >= items.length - 1}
                              >
                                <ChevronDown className="w-3 h-3" />
                              </Button>
                            </div>

                            {item.image_url && (
                              <div className="w-12 h-12 rounded-lg overflow-hidden border border-border flex-shrink-0 bg-muted">
                                <img
                                  src={resolveImageUrl(item.image_url)}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    console.error('[AdminFullMenu] Image load failed:', item.image_url);
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{item.name}</span>

                                {Array.isArray(item.badges) && item.badges.map((badge) => (
                                  <Badge key={badge} variant="outline" className="text-xs">
                                    {BADGE_OPTIONS.find((b) => b.value === badge)?.label || badge}
                                  </Badge>
                                ))}

                                {item.image_url && (
                                  <ImageIcon className="w-3 h-3 text-muted-foreground" />
                                )}

                                {!item.is_active && (
                                  <Badge variant="outline" className="text-xs">
                                    Disattivo
                                  </Badge>
                                )}
                              </div>

                              {item.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                  {item.description}
                                </p>
                              )}

                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                {renderPriceDisplay(item)}
                                <Badge variant="outline" className="text-xs">
                                  {formatWeekdayAvailabilityLabel(item.available_weekdays)}
                                </Badge>
                              </div>
                            </div>

                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setEditItemDialog({ categoryId: category.id, item: { ...item } })}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => deleteItem(category.id, item.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}

                        <Button
                          variant="outline"
                          className="w-full mt-2"
                          onClick={() => {
                            setTargetCategoryId(category.id);
                            setNewItemDialog(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi Piatto
                        </Button>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              );
            })}
          </div>
        )}

        <Dialog open={newCategoryDialog} onOpenChange={setNewCategoryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuova Categoria</DialogTitle>
              <DialogDescription>
                Aggiungi una nuova categoria al menu completo
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="new-category-name">Nome Categoria</Label>
                <Input
                  id="new-category-name"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Es: Primi di terra"
                />
              </div>

              <div>
                <Label htmlFor="new-category-slug">Slug (opzionale)</Label>
                <Input
                  id="new-category-slug"
                  value={newCategory.slug}
                  onChange={(e) => setNewCategory((prev) => ({ ...prev, slug: e.target.value }))}
                  placeholder="Es: primi-di-terra"
                />
              </div>

              <div>
                <Label htmlFor="new-category-description">Descrizione</Label>
                <Textarea
                  id="new-category-description"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Breve descrizione categoria..."
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="new-category-active">Categoria attiva</Label>
                <Switch
                  id="new-category-active"
                  checked={newCategory.is_active}
                  onCheckedChange={(value) => setNewCategory((prev) => ({ ...prev, is_active: value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setNewCategoryDialog(false)}>
                Annulla
              </Button>
              <Button onClick={addCategory}>
                Aggiungi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editCategoryDialog} onOpenChange={() => setEditCategoryDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifica Categoria</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-category-name">Nome Categoria</Label>
                <Input
                  id="edit-category-name"
                  value={editCategoryDialog?.name || ''}
                  onChange={(e) =>
                    setEditCategoryDialog((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="edit-category-slug">Slug</Label>
                <Input
                  id="edit-category-slug"
                  value={editCategoryDialog?.slug || ''}
                  onChange={(e) =>
                    setEditCategoryDialog((prev) => ({ ...prev, slug: e.target.value }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="edit-category-description">Descrizione</Label>
                <Textarea
                  id="edit-category-description"
                  value={editCategoryDialog?.description || ''}
                  onChange={(e) =>
                    setEditCategoryDialog((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="edit-category-active">Categoria attiva</Label>
                <Switch
                  id="edit-category-active"
                  checked={editCategoryDialog?.is_active ?? true}
                  onCheckedChange={(value) =>
                    setEditCategoryDialog((prev) => ({ ...prev, is_active: value }))
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditCategoryDialog(null)}>
                Annulla
              </Button>

              <Button
                onClick={() => {
                  if (!editCategoryDialog?.name?.trim()) {
                    toast.error('Inserisci un nome categoria');
                    return;
                  }

                  updateCategory(editCategoryDialog.id, {
                    name: editCategoryDialog.name.trim(),
                    slug: editCategoryDialog.slug?.trim() || '',
                    description: editCategoryDialog.description?.trim() || '',
                    is_active: !!editCategoryDialog.is_active,
                  });

                  setEditCategoryDialog(null);
                  toast.success('Categoria aggiornata!');
                }}
              >
                Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={newItemDialog} onOpenChange={setNewItemDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nuovo Piatto</DialogTitle>
              <DialogDescription>Aggiungi un nuovo piatto alla categoria</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="new-item-name">Nome Piatto *</Label>
                <Input
                  id="new-item-name"
                  value={newItem.name}
                  onChange={(e) => setNewItem((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Es: Orecchiette al ragù"
                />
              </div>

              <div>
                <Label htmlFor="new-item-description">Descrizione</Label>
                <Textarea
                  id="new-item-description"
                  value={newItem.description}
                  onChange={(e) => setNewItem((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Breve descrizione del piatto..."
                />
              </div>

              <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                <Label className="text-sm font-semibold">Prezzo</Label>

                <div>
                  <Label htmlFor="new-item-price-type" className="text-xs text-muted-foreground">Tipo prezzo</Label>
                  <Select
                    value={newItem.price_type}
                    onValueChange={(value) => setNewItem((prev) => ({ ...prev, price_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portion">Prezzo porzione (300g)</SelectItem>
                      <SelectItem value="fixed">Prezzo fisso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="new-item-price-value" className="text-xs text-muted-foreground">Prezzo (€)</Label>
                  <Input
                    id="new-item-price-value"
                    type="number"
                    step="0.01"
                    min="0"
                    max="999.99"
                    value={newItem.price_value}
                    onChange={(e) => setNewItem((prev) => ({ ...prev, price_value: e.target.value }))}
                    placeholder="es. 8.50"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="new-item-show-price" className="text-xs text-muted-foreground">
                    Mostra prezzo
                  </Label>
                  <Switch
                    id="new-item-show-price"
                    checked={newItem.show_price}
                    onCheckedChange={(value) => setNewItem((prev) => ({ ...prev, show_price: value }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="new-item-active" className="text-xs text-muted-foreground">
                    Piatto attivo
                  </Label>
                  <Switch
                    id="new-item-active"
                    checked={newItem.is_active}
                    onCheckedChange={(value) => setNewItem((prev) => ({ ...prev, is_active: value }))}
                  />
                </div>

                <WeekdaySelector
                  value={newItem.available_weekdays}
                  onChange={(value) => setNewItem((prev) => ({ ...prev, available_weekdays: value }))}
                />
              </div>

              <div>
                <Label>Badge</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {BADGE_OPTIONS.map((badge) => (
                    <Badge
                      key={badge.value}
                      variant={newItem.badges.includes(badge.value) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        setNewItem((prev) => ({
                          ...prev,
                          badges: prev.badges.includes(badge.value)
                            ? prev.badges.filter((b) => b !== badge.value)
                            : [...prev.badges, badge.value]
                        }));
                      }}
                    >
                      {badge.label}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Immagine del piatto</Label>
                <div className="mt-2 space-y-3">
                  {newItem.image_url ? (
                    <div className="relative w-full h-32 rounded-lg overflow-hidden border border-border bg-muted">
                      <img
                        src={resolveImageUrl(newItem.image_url)}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('[AdminFullMenu] New item image load failed:', newItem.image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7"
                        onClick={async () => {
                          const ok = await deleteImage(newItem.image_url);
                          if (ok) {
                            setNewItem((prev) => ({ ...prev, image_url: null }));
                          }
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploadingImage ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Clicca per caricare</p>
                            <p className="text-xs text-muted-foreground">JPG, PNG, WebP (max 5MB)</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={handleNewItemImageUpload}
                        disabled={uploadingImage}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setNewItemDialog(false)} disabled={uploadingImage}>
                Annulla
              </Button>
              <Button onClick={addItem} disabled={uploadingImage}>
                Aggiungi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editItemDialog} onOpenChange={() => setEditItemDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Modifica Piatto</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-item-name">Nome Piatto</Label>
                <Input
                  id="edit-item-name"
                  value={editItemDialog?.item?.name || ''}
                  onChange={(e) =>
                    setEditItemDialog((prev) => ({
                      ...prev,
                      item: { ...prev.item, name: e.target.value },
                    }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="edit-item-description">Descrizione</Label>
                <Textarea
                  id="edit-item-description"
                  value={editItemDialog?.item?.description || ''}
                  onChange={(e) =>
                    setEditItemDialog((prev) => ({
                      ...prev,
                      item: { ...prev.item, description: e.target.value },
                    }))
                  }
                />
              </div>

              <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                <Label className="text-sm font-semibold">Prezzo</Label>

                <div>
                  <Label htmlFor="edit-item-price-type" className="text-xs text-muted-foreground">Tipo prezzo</Label>
                  <Select
                    value={editItemDialog?.item?.price_type || 'portion'}
                    onValueChange={(value) =>
                      setEditItemDialog((prev) => ({
                        ...prev,
                        item: { ...prev.item, price_type: value },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portion">Prezzo porzione (300g)</SelectItem>
                      <SelectItem value="fixed">Prezzo fisso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit-item-price-value" className="text-xs text-muted-foreground">Prezzo (€)</Label>
                  <Input
                    id="edit-item-price-value"
                    type="number"
                    step="0.01"
                    min="0"
                    max="999.99"
                    value={editItemDialog?.item?.price_value ?? ''}
                    onChange={(e) =>
                      setEditItemDialog((prev) => ({
                        ...prev,
                        item: {
                          ...prev.item,
                          price_value: e.target.value === '' ? '' : e.target.value,
                        },
                      }))
                    }
                    placeholder="es. 8.50"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-item-show-price" className="text-xs text-muted-foreground">
                    Mostra prezzo
                  </Label>
                  <Switch
                    id="edit-item-show-price"
                    checked={editItemDialog?.item?.show_price ?? true}
                    onCheckedChange={(value) =>
                      setEditItemDialog((prev) => ({
                        ...prev,
                        item: { ...prev.item, show_price: value },
                      }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-item-active" className="text-xs text-muted-foreground">
                    Piatto attivo
                  </Label>
                  <Switch
                    id="edit-item-active"
                    checked={editItemDialog?.item?.is_active ?? true}
                    onCheckedChange={(value) =>
                      setEditItemDialog((prev) => ({
                        ...prev,
                        item: { ...prev.item, is_active: value },
                      }))
                    }
                  />
                </div>

                <WeekdaySelector
                  value={editItemDialog?.item?.available_weekdays}
                  onChange={(value) =>
                    setEditItemDialog((prev) => ({
                      ...prev,
                      item: { ...prev.item, available_weekdays: value },
                    }))
                  }
                />
              </div>

              <div>
                <Label>Badge</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {BADGE_OPTIONS.map((badge) => (
                    <Badge
                      key={badge.value}
                      variant={(editItemDialog?.item?.badges || []).includes(badge.value) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        setEditItemDialog((prev) => ({
                          ...prev,
                          item: {
                            ...prev.item,
                            badges: (prev.item.badges || []).includes(badge.value)
                              ? prev.item.badges.filter((b) => b !== badge.value)
                              : [...(prev.item.badges || []), badge.value]
                          }
                        }));
                      }}
                    >
                      {badge.label}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Immagine del piatto</Label>
                <div className="mt-2 space-y-3">
                  {editItemDialog?.item?.image_url ? (
                    <div className="relative w-full h-32 rounded-lg overflow-hidden border border-border bg-muted">
                      <img
                        src={resolveImageUrl(editItemDialog.item.image_url)}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('[AdminFullMenu] Edit item image load failed:', editItemDialog.item.image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7"
                        onClick={async () => {
                          const ok = await deleteImage(editItemDialog.item.image_url);
                          if (ok) {
                            setEditItemDialog((prev) => ({
                              ...prev,
                              item: { ...prev.item, image_url: null },
                            }));
                          }
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploadingImage ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Clicca per caricare</p>
                            <p className="text-xs text-muted-foreground">JPG, PNG, WebP (max 5MB)</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={handleEditItemImageUpload}
                        disabled={uploadingImage}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditItemDialog(null)}>
                Annulla
              </Button>

              <Button
                disabled={uploadingImage}
                onClick={() => {
                  if (!editItemDialog?.item?.name?.trim()) {
                    toast.error('Inserisci un nome piatto');
                    return;
                  }

                  const priceValue = parsePrice(editItemDialog.item.price_value);

                  if (
                    editItemDialog.item.price_value !== '' &&
                    editItemDialog.item.price_value !== null &&
                    priceValue === null
                  ) {
                    toast.error('Prezzo non valido');
                    return;
                  }

                  if (priceValue !== null && (priceValue <= 0 || priceValue > 999.99)) {
                    toast.error('Il prezzo deve essere tra 0.01 e 999.99');
                    return;
                  }

                  updateItem(editItemDialog.categoryId, editItemDialog.item.id, {
                    ...editItemDialog.item,
                    name: editItemDialog.item.name.trim(),
                    description: editItemDialog.item.description?.trim() || '',
                    price_value: editItemDialog.item.price_value === '' ? '' : priceValue,
                    available_weekdays: normalizeAvailableWeekdays(editItemDialog.item.available_weekdays),
                  });

                  setEditItemDialog(null);
                  toast.success('Piatto aggiornato!');
                }}
              >
                Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
